import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Card, Button, Collapse, Tooltip, Tag, Pagination } from "antd";
import moment from "moment-timezone";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import { getErrorMessage } from "src/utils/errors";
import { EMPLOYEE_ROLES } from "@src/appV2/Employee/constants";
import { SessionType } from "src/modules/interface";
import { logEvent } from "@src/appV2/lib/analytics";
import { ADMIN_EVENTS } from "src/constants/firebaseEvents";
import { getAgentLicenses, getWorkerLicensesCount } from "./api";
import { ILicenseDetails, LICENSE_STATUSES, IWorkerLicensesCount } from "./interfaces";
import { AddLicense, LicenseForm } from "./addLicense";
import { LICENSE_STATUS_TAG_COLORS, LICENSE_STATUSES_VALUE_MAPPING } from "./constants";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useGetQualifications } from "@src/appV2/Admin/Qualifications/api/useGetQualifications";
import { getQualificationLabel } from "@src/appV2/Admin/Qualifications/utils/getQualificationLabel";

const { Panel } = Collapse;

export const LicenseManager: React.FC<{
  agentId: string;
  agent: { tmz: string; email: string; isTestAccount: boolean };
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line react/function-component-definition
}> = ({ agentId, agent }) => {
  const { data: qualificationsData, isLoading: isLoadingQualifications } = useGetQualifications();
  const [showAllLicenses, setShowAllLicenses] = useState<boolean>(false);
  const [licenses, setLicenses] = useState<ILicenseDetails[]>([]);
  const [licensesCount, setLicensesCount] = useState<IWorkerLicensesCount>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [openAddLicense, setOpenAddLicense] = useState<boolean>(false);
  const [selectedLicense, setSelectedLicense] = useState<ILicenseDetails | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const userRoles = useSelector<SessionType, string[]>(
    (state) => state.session.adminProfile.roles || []
  );

  const userHasLicenseManagerRole = useMemo(() => {
    return userRoles.includes(EMPLOYEE_ROLES.WORKER_LICENSE_MANAGER);
  }, [userRoles]);

  const getLicensesCount = async () => {
    let getAllLicenses = showAllLicenses;
    try {
      const countResponse = await getWorkerLicensesCount(agentId);
      let hasUnArchivedLicenses = false;
      for (const licenseStatus in countResponse) {
        if (licenseStatus !== LICENSE_STATUSES.ARCHIVED && countResponse[licenseStatus] > 0) {
          hasUnArchivedLicenses = true;
          break;
        }
      }
      setLicensesCount(countResponse);
      if (!hasUnArchivedLicenses && countResponse?.ARCHIVED && !showAllLicenses) {
        getAllLicenses = true;
        setShowAllLicenses(true);
      }
    } catch (error) {
      showErrorToast("Error getting Licenses count: " + getErrorMessage(error));
    }
    await getLicenses(getAllLicenses);
  };
  const totalLicenses = useMemo(() => {
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-shadow
    let totalLicenses = 0;
    for (const status in licensesCount) {
      if (status === LICENSE_STATUSES.ARCHIVED && !showAllLicenses) {
        continue;
      }
      totalLicenses += licensesCount[status];
    }
    return totalLicenses;
  }, [licensesCount, showAllLicenses]);

  const getLicenses = async (getAllLicenses: boolean) => {
    setOpenAddLicense(false);
    setLoading(true);
    try {
      const response = await getAgentLicenses(agentId, getAllLicenses, {
        pageNumber: currentPage,
        pageSize: 10,
      });
      setLicenses(response);
    } catch (error) {
      showErrorToast("Error getting Licenses: " + getErrorMessage(error));
    }
    setLoading(false);
  };

  const loadAgentLicenses = async () => {
    setOpenAddLicense(false);
    setLoading(true);
    await getLicensesCount();
    setLoading(false);
  };

  useEffect(() => {
    void loadAgentLicenses();
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, currentPage]);

  const handleAddLicenseModal = (value: boolean) => {
    setSelectedLicense(undefined);
    setOpenAddLicense(value);
    if (value) {
      logEvent(ADMIN_EVENTS.ADD_LICENSE_BEGUN, {
        hcp_id: agentId,
        license_manager: userHasLicenseManagerRole,
      });
    }
  };

  const handleEditLicense = (license: ILicenseDetails) => {
    setSelectedLicense(license);
    setOpenAddLicense(true);
    logEvent(ADMIN_EVENTS.LICENSE_EDITED, {
      hcp_id: agentId,
      license_status: license.status,
      days_until_expired: moment(license.expiresAt).diff(moment(), "days"),
      context: "drawer",
    });
  };

  return (
    <Card
      title="Licenses"
      extra={
        <>
          {licensesCount?.ARCHIVED && (
            <Button
              data-testid="show-all-licenses-button"
              type="link"
              style={{ paddingRight: "16px" }}
              onClick={() => {
                setShowAllLicenses(!showAllLicenses);
                setCurrentPage(1);
                void getLicenses(!showAllLicenses);
              }}
            >
              {showAllLicenses ? "Show Recent" : "Show All"}
            </Button>
          )}
          <Tooltip placement="top" title="Add a License">
            <Button
              data-testid="add-license-button"
              aria-label="Add new License"
              type="primary"
              shape="circle"
              icon={<PlusOutlined />}
              onClick={() => {
                handleAddLicenseModal(true);
              }}
            />
          </Tooltip>
        </>
      }
      loading={loading || isLoadingQualifications}
    >
      <Collapse>
        {licenses?.length ? (
          licenses.map((license) => {
            return (
              <Panel
                header={`${getQualificationLabel(
                  license.qualification,
                  qualificationsData?.data ?? []
                )} - ${license.state} ${license.multiState ? "(multi-state)" : ""}`}
                key={license.id}
                extra={
                  <>
                    <Tag color={LICENSE_STATUS_TAG_COLORS[license.status] ?? "default"}>
                      {LICENSE_STATUSES_VALUE_MAPPING[license.status]}
                    </Tag>
                    {userHasLicenseManagerRole && (
                      <Button
                        icon={<EditOutlined />}
                        style={{ borderRadius: "4px", height: "24px" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditLicense(license);
                        }}
                        data-testid={`edit-license-${license.number}`}
                      />
                    )}
                  </>
                }
              >
                <LicenseForm
                  qualifications={qualificationsData?.data ?? []}
                  licenseDetailsMode={true}
                  license={license}
                  userHasLicenseManagerRole={userHasLicenseManagerRole}
                  showFilePreview={false}
                />
              </Panel>
            );
          })
        ) : (
          <>
            <Panel
              collapsible="disabled"
              header={
                <span style={{ margin: "auto", color: "black" }}>
                  {!showAllLicenses && licensesCount?.ARCHIVED
                    ? "no recent licenses"
                    : "no licenses added"}
                </span>
              }
              key="no-licenses-added"
              showArrow={false}
            ></Panel>
          </>
        )}
      </Collapse>
      <br />
      <Pagination
        total={totalLicenses}
        current={currentPage}
        onChange={setCurrentPage}
        hideOnSinglePage={true}
        showSizeChanger={false}
        size="small"
        className="pagination"
      />
      {openAddLicense && (
        <AddLicense
          agent={agent}
          qualifications={qualificationsData?.data ?? []}
          licenseData={selectedLicense}
          isVisible={openAddLicense}
          loadAgentLicenses={loadAgentLicenses}
          agentId={agentId}
          handleCancel={() => {
            handleAddLicenseModal(false);
          }}
        />
      )}
    </Card>
  );
};
